/**
 * Card Component
 * Container component with semantic color tokens for automatic dark mode support.
 * Elevation maps to Shadow tokens; background uses bg.secondary so light/dark
 * mode is handled by Chakra's semantic token system — no useColorModeValue needed.
 */

import React from "react";
import { Box, BoxProps } from "@chakra-ui/react";

export interface CardProps extends Omit<BoxProps, "padding"> {
  children: React.ReactNode;
  elevation?: "none" | "subtle" | "small" | "medium" | "large";
  padding?: "none" | "small" | "medium" | "large";
  radius?: "small" | "medium" | "large";
  border?: boolean;
  interactive?: boolean;
}

const elevationMap: Record<NonNullable<CardProps["elevation"]>, string> = {
  none: "none",
  subtle: "0 1px 2px 0 rgba(0, 0, 0, 0.04)",
  small: "0 1px 3px 0 rgba(0, 0, 0, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.04)",
  medium: "0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.04)",
  large: "0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04)",
};

const paddingMap: Record<NonNullable<CardProps["padding"]>, { px: number | string; py: number | string }> = {
  none: { px: 0, py: 0 },
  small: { px: 3, py: 3 },
  medium: { px: 5, py: 4 },
  large: { px: 8, py: 6 },
};

const radiusMap: Record<NonNullable<CardProps["radius"]>, string> = {
  small: "6px",
  medium: "8px",
  large: "12px",
};

export function Card({
  children,
  elevation = "none",
  padding = "medium",
  radius = "medium",
  border = true,
  interactive = false,
  ...rest
}: CardProps) {
  const { px, py } = paddingMap[padding];

  return (
    <Box
      bg="bg.secondary"
      borderRadius={radiusMap[radius]}
      border={border ? "1px solid" : undefined}
      borderColor={border ? "border.subtle" : undefined}
      boxShadow={elevationMap[elevation]}
      cursor={interactive ? "pointer" : undefined}
      _hover={interactive ? { bg: "bg.tertiary", transition: "background 0.1s" } : undefined}
      px={px}
      py={py}
      {...rest}
    >
      {children}
    </Box>
  );
}
